// Angebotsübersicht: markierte Angebote bei OTTO deaktivieren (offline) oder wieder aktivieren.
//
// RPC otto_enqueue_set_active_status → Worker-Job otto.set_active_status (POST
// /v5/products/active-status). Deaktivieren nimmt das Angebot von otto.de, es bleibt in OTTO und in
// der App erhalten. Bestätigung im Modal, kein Browser-Dialog.

import type { SupabaseClient } from '@supabase/supabase-js';
import { openModal, showToast } from '@vario-connect/frontend-design';
import { awaitJob } from '../../lib/await-job.js';

export interface ActiveStatusRow { sku: string; status: string | null }

/** Welche Markierungen sich deaktivieren bzw. aktivieren lassen. Gelöschte zählen nirgends. */
export function splitForActiveStatus(rows: ReadonlyArray<ActiveStatusRow>): { deactivate: string[]; activate: string[] } {
  const deactivate: string[] = [];
  const activate: string[] = [];
  for (const r of rows) {
    if (r.status === 'deleted') continue;
    if (r.status === 'inactive') activate.push(r.sku);
    else deactivate.push(r.sku);
  }
  return { deactivate, activate };
}

interface ActiveStatusResult { succeeded?: number; failed?: Array<{ sku: string; message: string | null }>; pending?: number }

export function openActiveStatusModal(
  ctx: { supabase: SupabaseClient },
  skus: string[],
  active: boolean,
  onDone: () => void,
): void {
  const verb = active ? 'aktivieren' : 'deaktivieren';
  const n = skus.length;
  const list = skus.slice(0, 8).map((s) => `<li><code>${escapeHtml(s)}</code></li>`).join('') + (n > 8 ? `<li>… und ${n - 8} weitere</li>` : '');
  const hint = active
    ? 'Die Angebote werden auf otto.de wieder sichtbar und kaufbar, sofern Bestand vorhanden ist.'
    : 'Die Angebote verschwinden von otto.de und sind nicht mehr kaufbar. Sie bleiben bei OTTO und in der App erhalten und lassen sich hier wieder aktivieren.';
  const modal = openModal({
    title: `Bei OTTO ${verb}`,
    icon: active ? 'fa-eye' : 'fa-eye-slash',
    width: 480,
    body: `
      <div class="kl-modal-meta"><strong>${n} ${n === 1 ? 'Angebot' : 'Angebote'}</strong></div>
      <ul style="margin:6px 0 10px 18px;">${list}</ul>
      <div class="kl-form-hint">${hint}</div>
      <div class="kl-modal-error" id="otto-as-error" hidden></div>`,
    footer: `
      <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>
      <button type="button" class="v-button v-button--primary" data-action="confirm">
        <span class="v-icon fa-regular ${active ? 'fa-eye' : 'fa-eye-slash'}"></span>${n} bei OTTO ${verb}
      </button>`,
  });
  const btn = modal.el.querySelector<HTMLButtonElement>('[data-action="confirm"]');
  const errEl = modal.el.querySelector<HTMLElement>('#otto-as-error');
  btn?.addEventListener('click', async () => {
    btn.disabled = true;
    btn.innerHTML = `<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>Wird an OTTO übertragen …`;
    const { data, error } = await ctx.supabase.rpc('otto_enqueue_set_active_status', { _skus: skus, _active: active });
    if (error || typeof data !== 'string') {
      if (errEl) { errEl.hidden = false; errEl.textContent = `Fehlgeschlagen: ${error?.message ?? 'keine Job-ID'}`; }
      btn.disabled = false;
      btn.textContent = `${n} bei OTTO ${verb}`;
      return;
    }
    try {
      const job = await awaitJob(ctx.supabase, data, { timeoutMs: 3 * 60_000, pollIntervalMs: 2500 });
      const r = (job.result ?? {}) as ActiveStatusResult;
      const failed = r.failed ?? [];
      if (job.status === 'failed') {
        showToast(`Bei OTTO ${verb} fehlgeschlagen: ${job.error ?? 'unbekannter Fehler'}`, 'error');
      } else if (failed.length > 0) {
        showToast(`${r.succeeded ?? 0} von ${n} ${active ? 'aktiviert' : 'deaktiviert'}, abgelehnt: ${failed.map((f) => f.sku).join(', ')}`, 'error');
      } else {
        const pending = r.pending ?? 0;
        showToast(`${n - pending} von ${n} bei OTTO ${active ? 'aktiviert' : 'deaktiviert'}${pending > 0 ? `, ${pending} noch in Verarbeitung` : ''}. Der Status in der Liste folgt mit dem nächsten Abruf.`, 'success');
      }
    } catch {
      showToast('OTTO braucht länger als erwartet. Ergebnis steht gleich im Log.', 'info');
    }
    modal.close();
    onDone();
  });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
